<template>
    <div class="gps">
        <div class="gps__header">
            <div class="gps__header-left">
                <h3 class="gps__title">GPS ДАНІ</h3>
                <div class="gps__y-labels">
                    <span class="gps__y-label">15 км</span>
                    <span class="gps__y-divider">|</span>
                    <span class="gps__y-label">10 км</span>
                    <span class="gps__y-divider">|</span>
                    <span class="gps__y-label">5 км</span>
                </div>
            </div>
            <div class="gps__header-right">
                <select v-model="selectedGroupId" class="gps__select" @change="loadGpsData">
                    <option :value="null">Всі команди</option>
                    <option v-for="group in groups" :key="group.group_id" :value="group.group_id">
                        {{ group.name }}
                    </option>
                </select>
            </div>
        </div>

        <div ref="chartContainer" class="gps__canvas"></div>

        <div class="gps__legend">
            <label v-for="param in parameters" :key="param.key" class="gps__legend-item" :class="{'gps__legend-item--active': param.visible}">
                <input type="checkbox" v-model="param.visible" @change="updateChart" class="gps__checkbox" />
                <span class="gps__legend-dot" :style="{ background: param.color }"></span>
                <span class="gps__legend-text">{{ param.label }}</span>
            </label>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, reactive } from 'vue'
import * as echarts from 'echarts'
import { getAggregatedGpsData, getAllGroups } from '@/services/apiRequests'

export default {
    name: 'GpsDataChart',

    setup() {
        const chartContainer = ref(null)
        let chart = null
        let resizeObserver = null

        const groups = ref([])
        const selectedGroupId = ref(null)
        const gpsData = ref([])

        const parameters = reactive([
            { key: 'totalDistance', label: 'Загальна дистанція (км)', color: '#8FC408', visible: true },
            { key: 'averageSpeed', label: 'Середня швидкість (км/год)', color: '#339989', visible: true },
            { key: 'speedZone', label: 'Швидкісна зона (15.5-20)', color: '#C59014', visible: true },
            { key: 'sprintCount', label: 'Кількість спринтів (> 20 км/год)', color: '#DE3A73', visible: true },
            { key: 'speedDistance', label: 'Швидкісний навантажний індекс', color: '#7C6BF0', visible: false }
        ])

        const loadGpsData = async () => {
            try {
                const data = await getAggregatedGpsData(selectedGroupId.value, null, null)
                gpsData.value = data || []
                updateChart()
            } catch (e) {
                gpsData.value = []
            }
        }

        const updateChart = () => {
            if (!chart) return

            const dates = gpsData.value.map(d => {
                if (!d.date) return ''
                const dateObj = new Date(d.date)
                return `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}`
            })

            const series = parameters
                .filter(p => p.visible)
                .map(p => ({
                    name: p.label,
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 7,
                    lineStyle: { width: 2.5, color: p.color },
                    itemStyle: { color: p.color, borderWidth: 2, borderColor: '#1c1c1e' },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: p.color + '30' },
                            { offset: 1, color: p.color + '00' }
                        ])
                    },
                    data: gpsData.value.map(d => d[p.key] || 0)
                }))

            const option = {
                backgroundColor: 'transparent',
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(20, 20, 20, 0.95)',
                    borderColor: 'rgba(255,255,255,0.08)',
                    borderRadius: 10,
                    padding: [10, 14],
                    textStyle: { color: '#e0e0e0', fontSize: 11, fontFamily: 'inherit' },
                    axisPointer: {
                        lineStyle: { color: 'rgba(255,255,255,0.08)', type: 'dashed' }
                    }
                },
                grid: {
                    top: 15,
                    left: 45,
                    right: 15,
                    bottom: 28
                },
                xAxis: {
                    type: 'category',
                    data: dates,
                    axisLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
                    axisLabel: { color: 'rgba(255,255,255,0.35)', fontSize: 10, margin: 10 },
                    axisTick: { show: false },
                    splitLine: { show: false }
                },
                yAxis: {
                    type: 'value',
                    axisLine: { show: false },
                    axisLabel: { color: 'rgba(255,255,255,0.25)', fontSize: 10 },
                    axisTick: { show: false },
                    splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)', type: 'dashed' } }
                },
                series
            }

            chart.setOption(option, true)
        }

        onMounted(async () => {
            try {
                const groupsData = await getAllGroups()
                groups.value = groupsData.data || groupsData || []
            } catch (e) {
                groups.value = []
            }

            if (chartContainer.value) {
                chart = echarts.init(chartContainer.value)
                resizeObserver = new ResizeObserver(() => {
                    if (chart) chart.resize()
                })
                resizeObserver.observe(chartContainer.value)
            }

            await loadGpsData()
        })

        onBeforeUnmount(() => {
            if (resizeObserver) resizeObserver.disconnect()
            if (chart) {
                chart.dispose()
                chart = null
            }
        })

        return { chartContainer, groups, selectedGroupId, parameters, loadGpsData, updateChart }
    }
}
</script>

<style scoped>
.gps {
    background: linear-gradient(180deg, #1c1c1e 0%, #161618 100%);
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: 16px;
    padding: 16px 18px;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.gps__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
}

.gps__header-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.gps__title {
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
}

.gps__y-labels {
    display: flex;
    gap: 6px;
    align-items: center;
}

.gps__y-label {
    color: rgba(255, 255, 255, 0.3);
    font-size: 10px;
}

.gps__y-divider {
    color: rgba(255, 255, 255, 0.1);
    font-size: 10px;
}

.gps__select {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ccc;
    padding: 5px 10px;
    border-radius: 8px;
    font-size: 11px;
    outline: none;
    cursor: pointer;
    transition: border-color 0.2s;
}

.gps__select:hover {
    border-color: rgba(143, 196, 8, 0.4);
}

.gps__canvas {
    flex: 1;
    min-height: 180px;
}

.gps__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.gps__legend-item {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.2s;
}

.gps__legend-item--active {
    opacity: 1;
}

.gps__checkbox {
    display: none;
}

.gps__legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.gps__legend-text {
    color: rgba(255, 255, 255, 0.6);
    font-size: 10px;
    white-space: nowrap;
}
</style>
